import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import fetchWithAuth from '../api/fetchWithAuth';
import apiUrls from '../api/apiUrls';

const AmbientContext = createContext();

export const AmbientProvider = ({ children }) => {
  const [tracks, setTracks] = useState([]);
  const [currentTrackId, setCurrentTrackId] = useState(null);
  const [volume, setVolume] = useState(0.5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const audioRef = useRef(new Audio());

  // Inicializar configuración de audio
  useEffect(() => {
    audioRef.current.crossOrigin = "anonymous";
    audioRef.current.loop = true;
  }, []);

  // Cargar pistas disponibles
  useEffect(() => {
    const fetchTracks = async () => {
      try {
        const response = await fetchWithAuth(apiUrls.ambientTracksApi);
        if (response.ok) {
          const data = await response.json();
          setTracks(data);
        }
      } catch (error) {
        console.error('Error fetching ambient tracks:', error);
      }
    };
    fetchTracks();
  }, []);

  // Actualizar volumen
  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  // Gestionar cambio de pista y reproducción
  useEffect(() => {
    // Si ya estamos desbloqueados y la pista está seteada, intentar reproducir
    if (currentTrackId && tracks.length > 0) {
      const track = tracks.find(t => t.id === currentTrackId);
      if (track) {
        const fileName = track.url.split('/').pop();
        const fullUrl = `${apiUrls.ambientFiles}${fileName}`;

        if (audioRef.current.src !== fullUrl) {
          audioRef.current.src = fullUrl;
          audioRef.current.load();
        }

        // Si ya está desbloqueado o lo forzamos al montar, intentamos play
        if (isPlaying) {
          audioRef.current.play().catch(e => {
            if (e.name !== 'AbortError') console.error("Ambient play error:", e);
          });
        }
      }
    } else {
      audioRef.current.pause();
    }
  }, [currentTrackId, tracks, isPlaying]); // Quitamos 'isUnlocked' como dependencia para que se intente siempre que cambie track/isPlaying

  // Desbloquear audio por interacción
  const unlockAudio = useCallback(() => {
    if (!isUnlocked) {
      // Intentar un play/pause rápido para desbloquear el contexto de audio
      audioRef.current.play().then(() => {
        if (!isPlaying) audioRef.current.pause();
        setIsUnlocked(true);
        console.log("[Ambient] Audio context unlocked");
      }).catch(e => {
        console.log("[Ambient] Interaction didn't unlock audio yet", e);
      });
    }
  }, [isUnlocked, isPlaying]);

  useEffect(() => {
    const handleInteraction = () => {
      unlockAudio();
    };

    if (!isUnlocked) {
      window.addEventListener('click', handleInteraction);
      window.addEventListener('touchstart', handleInteraction);
    }

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [isUnlocked, unlockAudio]);

  const selectTrack = (id) => {
    setCurrentTrackId(id);
    if (id) setIsPlaying(true);
    else setIsPlaying(false);
  };

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else if (currentTrackId && isUnlocked) {
      audioRef.current.play().catch(e => console.error("Manual ambient play error:", e));
    }
    setIsPlaying(!isPlaying);
  };

  const updateVolume = (newVolume) => {
    setVolume(newVolume);
  };

  const value = {
    tracks,
    currentTrackId,
    volume,
    isPlaying,
    isUnlocked,
    selectTrack,
    togglePlay,
    updateVolume,
    setVolume,
    setCurrentTrackId,
    setIsPlaying
  };

  return (
    <AmbientContext.Provider value={value}>
      {children}
    </AmbientContext.Provider>
  );
};

export const useAmbient = () => {
  const context = useContext(AmbientContext);
  if (!context) {
    throw new Error('useAmbient must be used within an AmbientProvider');
  }
  return context;
};
